/**
 * The consent seam.
 *
 * There is no cookie banner yet, so the default posture is opt-out: we track
 * unless the visitor has explicitly withdrawn consent. This file is the ONLY
 * place that knows how that decision is stored and how it reaches PostHog —
 * a future banner calls `setAnalyticsConsent()` and touches nothing else.
 */

const CONSENT_COOKIE = 'outpoll_analytics_consent';
const CONSENT_MAX_AGE_SEC = 365 * 24 * 60 * 60; // 1 year, matching the locale cookie

export type AnalyticsConsent = 'granted' | 'denied';

/** Listeners let the provider react without importing `posthog-js` eagerly. */
type ConsentListener = (consent: AnalyticsConsent) => void;
const listeners = new Set<ConsentListener>();

function readConsentCookie(): AnalyticsConsent | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]+)`));
  const value = match?.[1]?.trim();
  return value === 'granted' || value === 'denied' ? value : undefined;
}

/**
 * The recorded decision, or `undefined` when the visitor has not been asked
 * yet. `AnalyticsConsentBanner` shows itself only in that last case.
 */
export function getAnalyticsConsent(): AnalyticsConsent | undefined {
  return readConsentCookie();
}

/**
 * Current consent state. Absent cookie means "not asked yet", which under the
 * opt-out posture counts as granted. Flip this single expression to switch the
 * product to opt-in.
 */
export function hasAnalyticsConsent(): boolean {
  return readConsentCookie() !== 'denied';
}

export function setAnalyticsConsent(granted: boolean): void {
  const consent: AnalyticsConsent = granted ? 'granted' : 'denied';
  if (typeof document !== 'undefined') {
    try {
      const isSecure = typeof location !== 'undefined' && location.protocol === 'https:';
      document.cookie = `${CONSENT_COOKIE}=${consent}; Path=/; Max-Age=${CONSENT_MAX_AGE_SEC}; SameSite=Lax${isSecure ? '; Secure' : ''}`;
    } catch {
      // A blocked cookie store must not break the caller — the in-memory
      // listeners below still apply the change for this page view.
    }
  }
  for (const listener of listeners) listener(consent);
}

export function onAnalyticsConsentChange(listener: ConsentListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
